import { useCallback, useRef, useSyncExternalStore } from 'react'
import { flushSync } from 'react-dom'


export const PRINT_AREA_ATTR = 'data-print-area'
export const PRINTING_CLASS = 'is-printing'

const PAGE_RULE_ID = 'print-page-rule'

let printing = false
const listeners = new Set()

const subscribe = listener => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

const getSnapshot = () => printing

export function useIsPrinting() {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

const setPrintMode = value => {
  if (printing === value) return
  flushSync(() => {
    printing = value
    listeners.forEach(listener => listener())
  })
}

/**
 * Print a single element.
 *
 * @param {HTMLElement} node        the element to print
 * @param {object}      options
 * @param {'portrait'|'landscape'} options.orientation
 * @param {string}      options.margin  CSS @page margin, e.g. '12mm'
 * @param {string}      options.title   document title, which browsers use for the filename
 *                                      when the user picks "Save as PDF"
 */
export function printElement(node, { orientation = 'portrait', margin = '12mm', title } = {}) {
  if (!node) {
    setPrintMode(true)
    window.print()
    setPrintMode(false)
    return
  }

  const previousTitle = document.title
  if (title) document.title = title

  setPrintMode(true)
  node.setAttribute(PRINT_AREA_ATTR, '')
  document.body.classList.add(PRINTING_CLASS)

  const pageRule = document.createElement('style')
  pageRule.id = PAGE_RULE_ID
  pageRule.textContent = `@page { size: ${orientation}; margin: ${margin}; }`
  document.head.appendChild(pageRule)

  let cleaned = false
  const cleanup = () => {
    if (cleaned) return
    cleaned = true
    node.removeAttribute(PRINT_AREA_ATTR)
    document.body.classList.remove(PRINTING_CLASS)
    pageRule.remove()
    document.title = previousTitle
    window.removeEventListener('afterprint', cleanup)
    setPrintMode(false)
  }

  window.addEventListener('afterprint', cleanup)
  window.print()
  cleanup()
}

export default function usePrintArea(options = {}) {
  const printRef = useRef(null)
  const { orientation, margin, title } = options

  const print = useCallback(
    () => printElement(printRef.current, { orientation, margin, title }),
    [orientation, margin, title],
  )

  return { printRef, print }
}
